import { format } from 'date-fns'
import { isValid } from 'iban'
import has from 'lodash-es/has'
import isEqual from 'lodash-es/isEqual'
import * as Yup from 'yup'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../constants/date'
import { FILE_SCOPE_REQUEST } from '../../constants/file'
import { REG_EXP_BIC } from '../../constants/regExp'
import { isValueObjectType, isValueStringType } from '../../utils/common'
import { parseAmount } from '../../utils/number'
import { toSupplierSelectItem } from '../Suppliers'

import {
  INVOICE_FIELD_LABEL_RECIPIENT,
  INVOICE_FIELD_LABEL_SUPPLIER,
  INVOICE_FIELD_NAME_NOTES,
  INVOICE_FIELD_NAME_RECIPIENT,
  INVOICE_FIELD_NAME_SUPPLIER,
  INVOICE_FIELD_TYPE_BIC,
  INVOICE_FIELD_TYPE_DATE,
  INVOICE_FIELD_TYPE_FLOAT,
  INVOICE_FIELD_TYPE_IBAN,
  INVOICE_FIELD_TYPE_INT,
  INVOICE_FIELD_TYPE_READONLY,
  INVOICE_FIELD_TYPE_SELECT,
  INVOICE_FIELD_TYPE_STR,
  INVOICE_FIELD_TYPE_SUPPLIER,
  INVOICE_MODAL_FIELD_AMOUNT_MIN,
  INVOICE_MODAL_FIELD_BIC_MAX_LENGTH,
  INVOICE_MODAL_FIELD_IBAN_MAX_LENGTH,
  INVOICE_MODAL_FIELD_RECIPIENT_MAX_LENGTH,
} from './InvoiceModal.constants'

function checkIsRecipientField(field) {
  return (
    field.name === INVOICE_FIELD_NAME_RECIPIENT ||
    field.label === INVOICE_FIELD_LABEL_RECIPIENT
  )
}

function checkIsVisibleField(field) {
  return (
    field.show_in_form &&
    field.visible &&
    field.type !== INVOICE_FIELD_TYPE_READONLY
  )
}

function getSelectValidationSchema(required, t) {
  return Yup.lazy(value => {
    if (value == null) {
      return required
        ? Yup.mixed().nullable().required(true)
        : Yup.mixed().nullable()
    }

    if (!has(value, 'value')) {
      return Yup.mixed().typeError(t('Error.IncorrectValueKey'))
    }

    const schema = Yup.object({
      value: required ? Yup.number().required(true) : Yup.number().nullable(),
    }).nullable()

    return required ? schema.required(true) : schema
  })
}

function getNumberValidationSchema(field, t) {
  const maxLength = getInvoiceModalFieldMaxLength(field)

  const baseSchema = Yup.number()
    .transform((value, originalValue) =>
      originalValue === EMPTY_STRING || originalValue == null
        ? null
        : parseAmount(originalValue)
    )
    .nullable()
    .typeError(t('Error.IncorrectValueKey'))
    .min(INVOICE_MODAL_FIELD_AMOUNT_MIN, t('Error.IncorrectValueKey'))

  const schemaWithMaxLength =
    maxLength != null
      ? baseSchema.test({
          message: t('Error.MaxLengthKey', { length: maxLength }),
          name: 'maxLength',
          // Count digits only — the decimal separator and sign must not eat
          // into the limit (backend's max applies to digits, not the string).
          test: (value, context) =>
            context.originalValue == null ||
            context.originalValue === EMPTY_STRING ||
            String(context.originalValue).replace(/\D/g, EMPTY_STRING).length <=
              maxLength,
        })
      : baseSchema

  return field.required
    ? schemaWithMaxLength.required(true)
    : schemaWithMaxLength
}

function getStringValidationSchema(field, t) {
  const maxLength = getInvoiceModalFieldMaxLength(field)

  const baseSchema = field.validation?.min
    ? Yup.string().min(field.validation.min, true)
    : Yup.string()

  const schemaWithMaxLength =
    maxLength != null
      ? baseSchema.max(
          maxLength,
          t('Error.MaxLengthKey', { length: maxLength })
        )
      : baseSchema

  return field.required
    ? schemaWithMaxLength.required(true)
    : schemaWithMaxLength
}

function getDateFieldValidationSchema(field) {
  const schema = Yup.date().nullable()

  return field.required ? schema.required(true) : schema
}

function getBicFieldValidationSchema(field, t) {
  const schema = Yup.string()
    .matches(REG_EXP_BIC, t('Error.IncorrectBICKey'))
    .nullable()

  return field.required ? schema.required(true) : schema
}

function getIbanFieldValidationSchema(field, t) {
  const schema = Yup.string()
    .test({
      message: t('Error.IncorrectIBANKey'),
      test: value => !value || isValid(value),
    })
    .nullable()

  return field.required ? schema.required(true) : schema
}

function getFieldValidationSchema(field, t) {
  if (!checkIsVisibleField(field)) {
    return Yup.mixed().nullable().notRequired()
  }

  if (checkIsSupplierField(field)) {
    return getSelectValidationSchema(field.required, t)
  }

  switch (field.type) {
    case INVOICE_FIELD_TYPE_SELECT:
      return getSelectValidationSchema(field.required, t)
    case INVOICE_FIELD_TYPE_INT:
    case INVOICE_FIELD_TYPE_FLOAT:
      return getNumberValidationSchema(field, t)
    case INVOICE_FIELD_TYPE_DATE:
      return getDateFieldValidationSchema(field)
    case INVOICE_FIELD_TYPE_BIC:
      return getBicFieldValidationSchema(field, t)
    case INVOICE_FIELD_TYPE_IBAN:
      return getIbanFieldValidationSchema(field, t)
    case INVOICE_FIELD_TYPE_STR:
      return getStringValidationSchema(field, t)
    default:
      if (field.name === INVOICE_FIELD_NAME_NOTES) {
        return getStringValidationSchema(field, t)
      }

      return Yup.mixed().nullable().notRequired()
  }
}

function toApiValue(field, value) {
  if (checkIsSupplierField(field)) {
    return value?.value ?? null
  }

  if (field.type === INVOICE_FIELD_TYPE_DATE) {
    return value ? format(value, DATE_API_DATA_FORMAT_SHORT) : null
  }

  if (field.type === INVOICE_FIELD_TYPE_SELECT) {
    return value?.value ?? null
  }

  if (field.type === INVOICE_FIELD_TYPE_FLOAT) {
    return value === EMPTY_STRING || value == null
      ? null
      : trimTrailingDecimalZeros(value, '.')
  }

  return value !== EMPTY_STRING ? value : null
}

// Normalizes a numeric string: drops insignificant leading zeros from the
// integer part ("007" -> "7") and trailing zeros from the decimal part
// ("50.30"/"50,30" -> "50,3", "50,00" -> "50"), rendering the decimal with the
// given separator. Works on the string (no Number round-trip) so long values
// keep full precision instead of being re-padded with zeros once they exceed
// float64's range.
function trimTrailingDecimalZeros(value, separator = ',') {
  if (!isValueStringType(value)) {
    return value
  }

  const match = value.match(/^(-?)(\d+)(?:[.,](\d+))?$/)

  if (!match) {
    return value
  }

  const [, sign, integerPart, fraction = EMPTY_STRING] = match
  const normalizedInteger = integerPart.replace(/^0+(\d)/, '$1')
  const trimmedFraction = fraction.replace(/0+$/, EMPTY_STRING)

  return trimmedFraction
    ? `${sign}${normalizedInteger}${separator}${trimmedFraction}`
    : `${sign}${normalizedInteger}`
}

export function checkIsSupplierField(field) {
  return (
    field.type === INVOICE_FIELD_TYPE_SUPPLIER ||
    field.name === INVOICE_FIELD_NAME_SUPPLIER ||
    field.label === INVOICE_FIELD_LABEL_SUPPLIER
  )
}

export function checkIsBicField(field) {
  return field.type === INVOICE_FIELD_TYPE_BIC
}

export function checkIsIbanField(field) {
  return field.type === INVOICE_FIELD_TYPE_IBAN
}

export function getInvoiceModalFieldMaxLength(field) {
  // BIC and IBAN use the standard banking limits, not the backend's.
  if (checkIsBicField(field)) {
    return INVOICE_MODAL_FIELD_BIC_MAX_LENGTH
  }

  if (checkIsIbanField(field)) {
    return INVOICE_MODAL_FIELD_IBAN_MAX_LENGTH
  }

  if (field.validation?.max != null) {
    return field.validation.max
  }

  if (checkIsRecipientField(field)) {
    return INVOICE_MODAL_FIELD_RECIPIENT_MAX_LENGTH
  }
}

export function applyFieldDepends(field, formValues) {
  const { field: parentField, values } = field.depends || EMPTY_OBJECT

  if (!parentField) {
    return field
  }

  const rawValue = formValues[parentField]
  const parentValueKey = rawValue?.value ?? rawValue

  const parentValue = values?.find(v => v.value === parentValueKey)

  return parentValue?.params ? { ...field, ...parentValue.params } : field
}

export function getVisibleInvoiceModalFields(fields, formValues) {
  return fields
    .map(field => applyFieldDepends(field, formValues))
    .filter(checkIsVisibleField)
    .sort((a, b) => a.order - b.order)
}

export function getInvoiceModalInitialValues(fields) {
  return fields.reduce((acc, field) => {
    const initialValue = field.value ?? field.default ?? null

    if (field.type === INVOICE_FIELD_TYPE_DATE) {
      acc[field.name] = initialValue ? new Date(initialValue) : null
    } else if (field.type === INVOICE_FIELD_TYPE_SELECT) {
      acc[field.name] =
        initialValue != null
          ? (field.options?.find(opt => opt.value === initialValue) ?? null)
          : null
    } else if (checkIsSupplierField(field)) {
      // The backend may resolve the supplier value to a plain string — only
      // the object shape carries the data the select option needs.
      acc[field.name] =
        field.value && isValueObjectType(field.value)
          ? toSupplierSelectItem(field.value)
          : null
    } else if (
      field.type === INVOICE_FIELD_TYPE_FLOAT ||
      field.type === INVOICE_FIELD_TYPE_INT
    ) {
      acc[field.name] =
        initialValue != null ? String(initialValue) : EMPTY_STRING
    } else {
      acc[field.name] = initialValue ?? EMPTY_STRING
    }

    return acc
  }, {})
}

export function getInvoiceModalValidationSchema(fields, t) {
  return Yup.lazy(values =>
    Yup.object(
      fields.reduce((acc, field) => {
        const normalizedField = applyFieldDepends(field, values ?? EMPTY_OBJECT)

        acc[field.name] = getFieldValidationSchema(normalizedField, t)

        return acc
      }, {})
    )
  )
}

export function getInvoiceModalTrimmedValues(values, fields) {
  const next = { ...values }

  fields.forEach(field => {
    if (field.type === INVOICE_FIELD_TYPE_FLOAT) {
      // Float values are canonical raw strings from MoneyInput — keep the
      // dot as the decimal separator, the input renders the German format.
      next[field.name] = trimTrailingDecimalZeros(next[field.name], '.')
    }
  })

  return next
}

export function getNormalizedInvoiceSubmitValues(
  values,
  initialValues,
  fields
) {
  const clearedValues = { ...values }

  fields.forEach(field => {
    if (!field.visible) {
      clearedValues[field.name] = null
    }
  })

  return fields.reduce((acc, field) => {
    if (!isEqual(clearedValues[field.name], initialValues[field.name])) {
      acc[field.name] = toApiValue(field, clearedValues[field.name])
    }

    return acc
  }, {})
}

export function mergeInvoiceModalParsedValues(current, parsedValues) {
  if (!parsedValues) {
    return current
  }

  const next = { ...current }

  Object.keys(parsedValues).forEach(key => {
    if (key in next && parsedValues[key] != null) {
      next[key] = parsedValues[key]
    }
  })

  return next
}

export function getInvoiceFileDownloadParams(requestId) {
  return {
    ...(requestId ? { object_id: requestId, scope: FILE_SCOPE_REQUEST } : null),
    ensure_urls_signed: true,
  }
}
